"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ContactForm() {
  return;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  if (isSubmitted && !submitError) {
    return <div className="bg-primary-light border border-accent rounded-sm p-8 text-center shadow-medium">
        <div className="text-accent text-5xl mb-4"></div>
        <p className="text-foreground-primary text-lg mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="bg-transparent border-accent text-accent hover:bg-accent hover:text-background">
          Send Another Message
        </Button>
      </div>;
  }
  return <div ref={formRef} className="bg-primary-light border border-border rounded-sm p-8 shadow-medium">
      <h2 className="text-foreground-primary font-display text-4xl font-bold mb-2 text-center">
        Contact Form
      </h2>
      <p className="text-muted-foreground text-center mb-8">
        We'd love to hear from you. Fill out the form below to get in touch.
      </p>

      <Form onSubmit={handleSubmit}>
        <div className="space-y-6">
          <div className={`transition-all duration-300 ease-out ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{
          transitionDelay: "0ms"
        }}>
            <FormField control={control} name="name" rules={{
            required: "Name is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Your Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" className="bg-background border-border text-foreground focus-visible:border-accent focus-visible:ring-accent/20" {...field} />
                  </FormControl>
                  <FormDescription className="text-muted-foreground text-xs">
                    First and last name
                  </FormDescription>
                  <FormMessage />
                </FormItem>} />
          </div>

          <div className={`transition-all duration-300 ease-out ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{
          transitionDelay: "80ms"
        }}>
            <FormField control={control} name="email" rules={{
            required: "Email address is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="john@example.com" className="bg-background border-border text-foreground focus-visible:border-accent focus-visible:ring-accent/20" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          <div className={`transition-all duration-300 ease-out ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{
          transitionDelay: "160ms"
        }}>
            <FormField control={control} name="phone" rules={{
            pattern: {
              value: /^[\d\s\-\+\(\)]+$/,
              message: "Please enter a valid phone number"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+1 (555) 123-4567" className="bg-background border-border text-foreground focus-visible:border-accent focus-visible:ring-accent/20" {...field} />
                  </FormControl>
                  <FormDescription className="text-muted-foreground text-xs">
                    Optional – we may use this for follow-up
                  </FormDescription>
                  <FormMessage />
                </FormItem>} />
          </div>

          <div className={`transition-all duration-300 ease-out ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{
          transitionDelay: "240ms"
        }}>
            <FormField control={control} name="message" rules={{
            required: "Message is required",
            minLength: {
              value: 10,
              message: "Message must be at least 10 characters"
            },
            maxLength: {
              value: 1000,
              message: "Message must not exceed 1000 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Message</FormLabel>
                  <FormControl>
                    <textarea placeholder="How can we help you?" className="flex min-h-[120px] w-full rounded-md border border-border bg-background px-3 py-2 text-base shadow-xs transition-all outline-none placeholder:text-muted-foreground focus-visible:border-accent focus-visible:ring-accent/20 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 resize-none" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          {submitError && <div className={`p-4 bg-red-500/10 border border-red-500/20 rounded-sm transition-all duration-300 ease-out ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{
          transitionDelay: "320ms"
        }}>
              <p className="text-red-400 text-sm">{submitError}</p>
            </div>}

          <div className={`transition-all duration-300 ease-out ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{
          transitionDelay: "320ms"
        }}>
            <Button type="submit" disabled={isSubmitting} className="w-full bg-accent text-foreground-accent font-semibold hover:bg-accent-hover active:bg-accent-active transition-all duration-200 hover:scale-[1.03] shadow-soft">
              {isSubmitting ? <>
                  <Loader className="animate-spin mr-2 size-4" />
                  Sending...
                </> : "Send Message"}
            </Button>
          </div>
        </div>
      </Form>
    </div>;
}